import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Sun, Moon, Star, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PanchangPage = () => {
  const { i18n } = useTranslation();
  const isHindi = i18n.language === 'hi';
  const [panchang, setPanchang] = useState(null);

  useEffect(() => {
    const fetchPanchang = async () => {
      try {
        const res = await axios.get(`${API}/panchang/detailed`);
        setPanchang(res.data);
      } catch (err) { console.error(err); }
    };
    fetchPanchang();
  }, []);

  if (!panchang) return <div className="min-h-screen bg-[#0A0A0A] pt-24 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C9A84C]"></div></div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 px-6 md:px-12 lg:px-24 pb-16"
      style={{ backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/e9c10694-9f95-4971-9da3-c069ecc4c07f/images/e265648491dc6e2a9a322ca2ed91f76b659383224f50069da50c36f4e6acd44d.png)', backgroundSize: 'cover', backgroundAttachment: 'fixed' }}>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-serif text-[#F5F2EB] text-center mb-4" data-testid="panchang-heading">
          {isHindi ? 'आज का पंचांग' : "Today's Panchang"}
        </h1>
        <p className="text-[#C9A84C] text-center mb-12 text-lg">
          {isHindi ? panchang.day_hi : panchang.day_en}, {panchang.date}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <Moon className="w-6 h-6 text-[#C9A84C]" />
              <h2 className="text-xl font-serif text-[#F5F2EB]">{isHindi ? 'तिथि' : 'Tithi'}</h2>
            </div>
            <p className="text-[#F5F2EB] text-lg">{panchang.tithi}</p>
            <p className="text-[#A3A3A3] text-sm mt-1">{isHindi ? 'पक्ष' : 'Paksha'}: {panchang.paksha}</p>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <Star className="w-6 h-6 text-[#C9A84C]" />
              <h2 className="text-xl font-serif text-[#F5F2EB]">{isHindi ? 'नक्षत्र' : 'Nakshatra'}</h2>
            </div>
            <p className="text-[#F5F2EB] text-lg">{panchang.nakshatra}</p>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <Star className="w-6 h-6 text-[#C9A84C]" />
              <h2 className="text-xl font-serif text-[#F5F2EB]">{isHindi ? 'योग' : 'Yoga'}</h2>
            </div>
            <p className="text-[#F5F2EB] text-lg">{panchang.yoga}</p>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <Star className="w-6 h-6 text-[#C9A84C]" />
              <h2 className="text-xl font-serif text-[#F5F2EB]">{isHindi ? 'करण' : 'Karana'}</h2>
            </div>
            <p className="text-[#F5F2EB] text-lg">{panchang.karana}</p>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <Sun className="w-6 h-6 text-[#C9A84C]" />
              <h2 className="text-xl font-serif text-[#F5F2EB]">{isHindi ? 'सूर्योदय / सूर्यास्त' : 'Sunrise / Sunset'}</h2>
            </div>
            <p className="text-[#F5F2EB]">{isHindi ? 'सूर्योदय' : 'Sunrise'}: {panchang.sunrise}</p>
            <p className="text-[#F5F2EB]">{isHindi ? 'सूर्यास्त' : 'Sunset'}: {panchang.sunset}</p>
            <p className="text-[#A3A3A3] text-sm mt-1">{isHindi ? 'चंद्रोदय' : 'Moonrise'}: {panchang.moonrise}</p>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="w-6 h-6 text-red-400" />
              <h2 className="text-xl font-serif text-[#F5F2EB]">{isHindi ? 'राहुकाल' : 'Rahukaal'}</h2>
            </div>
            <p className="text-red-400 text-lg font-bold">{panchang.rahukaal}</p>
          </div>
        </div>

        <div className="mt-8 glass-card p-6">
          <h2 className="text-xl font-serif text-[#F5F2EB] mb-4">
            <Clock className="inline w-6 h-6 text-[#C9A84C] mr-2" />
            {isHindi ? 'शुभ मुहूर्त' : 'Auspicious Muhurat'}
          </h2>
          <div className="flex flex-wrap gap-3">
            {panchang.shubh_muhurat?.map((m, i) => (
              <span key={i} className="bg-[#C9A84C]/10 border border-[#C9A84C]/30 px-4 py-2 text-[#C9A84C] text-sm">{m}</span>
            ))}
          </div>
          <h3 className="text-[#F5F2EB] mt-4 mb-2">{isHindi ? 'अभिजित मुहूर्त' : 'Abhijit Muhurat'}</h3>
          <p className="text-[#C9A84C]">{panchang.abhijit_muhurat}</p>
        </div>
      </div>
    </div>
  );
};

export default PanchangPage;
